import { zodResolver } from '@hookform/resolvers/zod';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useIsMutating } from '@tanstack/react-query';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useThrottledCallback } from 'use-debounce';

import { POST_MUTATION_KEYS } from '@/features/api/constants/mutation';
import { usePollCreate } from '@/features/api/hooks/grapevine-poll-events/usePollCreate';
import { usePollDelete } from '@/features/api/hooks/grapevine-poll-events/usePollDelete';
import { useMediaPlaceholders } from '@/features/media/hooks/useMediaPlaceholder';
import { PostSubmitButton } from '@/features/post-editor/components/editor/PostSubmitButton';
import { PostForm } from '@/features/post-editor/components/PostForm';
import { MINISTRY_GEO_AREA_DEPTH } from '@/features/post-editor/constants/postForm';
import { useEditorModal } from '@/features/post-editor/hooks/useEditorModal';
import { useEditPost } from '@/features/post-editor/hooks/useEditPost';
import { useGetPostTypeFeature } from '@/features/post-editor/hooks/useGetPostFeature';
import {
  createOrganizationPostFormSchema,
  createPersonPostFormSchema
} from '@/features/post-editor/schemas/postForm';
import type { PostFormValues } from '@/features/post-editor/types/postForm';
import { toPollCreateForPostRequest } from '@/features/post-editor/utils/poll';
import { postToFormValues } from '@/features/post-editor/utils/postToFormValues';
import type { GenericPostDetail } from '@/features/posts/types/posts';
import type { Sender } from '@/features/sender-selection/types/sender';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import type { AppParam } from '@/navigation/types/navigation';
import { AppRoute } from '@/navigation/types/navigation';
import { StateView } from '@/shared/components/state-view/StateView';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

export type PostEditFormProps = {
  post: GenericPostDetail;
  navigation: NativeStackNavigationProp<AppParam>;
  sender: Sender;
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  body: { flex: 1 },
  submitButtonContainer: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 24
  }
});

export const PostEditForm: FC<PostEditFormProps> = ({
  post,
  navigation,
  sender
}) => {
  const theme = useAppTheme();

  const { t } = useTranslation(LocalizationNameSpace.POST_CREATION, {
    keyPrefix: 'screens.post-editor'
  });

  const isOrganizationPost = !isEmptyOrNull(post.organizationId);

  const initialValues = useMemo<PostFormValues>(
    () => postToFormValues({ post }),
    [post]
  );

  const postTypeFeature = useGetPostTypeFeature(initialValues.channelType);

  const postFormResolver = useMemo<Resolver<PostFormValues>>(() => {
    const schema = isOrganizationPost
      ? createOrganizationPostFormSchema({ t, postTypeFeature })
      : createPersonPostFormSchema({ t, postTypeFeature });
    return zodResolver(schema) as Resolver<PostFormValues>;
  }, [isOrganizationPost, t, postTypeFeature]);

  const form = useForm<PostFormValues>({
    resolver: postFormResolver,
    mode: 'onChange',
    defaultValues: initialValues
  });

  const modal = useEditorModal();
  const media = useMediaPlaceholders({
    initialImages: post.images?.map((image) => ({
      mediaItemId: image.id,
      mediaItem: image
    })),
    initialDocuments: post.attachments?.map((attachment) => ({
      mediaItemId: attachment.id,
      documentItem: attachment
    }))
  });

  const createPoll = usePollCreate();
  const deletePoll = usePollDelete();

  const navigateToDetail = useCallback(
    (targetPostId?: string) => {
      const id = targetPostId || post.id;
      if (isEmptyOrNull(id)) {
        navigation.pop();
        return;
      }
      navigation.replace(AppRoute.PostDetail, { id });
    },
    [navigation, post.id]
  );

  const handlePostSuccess = useCallback(
    (result: { postId?: string }) => {
      const newPostId = result.postId || post.id;
      if (!newPostId) {
        navigateToDetail();
        return;
      }
      const hadPoll = Boolean(post.poll);
      const currentPoll = form.getValues('poll');

      if (!hadPoll && !currentPoll) {
        navigateToDetail(newPostId);
        return;
      }

      if (hadPoll && currentPoll) {
        deletePoll.mutate(
          { postId: newPostId },
          {
            onSuccess: () => {
              createPoll.mutate(
                toPollCreateForPostRequest(currentPoll, newPostId),
                { onSettled: () => navigateToDetail(newPostId) }
              );
            },
            onError: () => navigateToDetail(newPostId)
          }
        );
        return;
      }

      if (hadPoll) {
        deletePoll.mutate(
          { postId: newPostId },
          {
            onSettled: () => navigateToDetail(newPostId)
          }
        );
        return;
      }

      if (currentPoll) {
        createPoll.mutate(toPollCreateForPostRequest(currentPoll, newPostId), {
          onSettled: () => navigateToDetail(newPostId)
        });
        return;
      }

      navigateToDetail(newPostId);
    },
    [createPoll, deletePoll, form, navigateToDetail, post.id, post.poll]
  );

  const editPost = useEditPost({
    postId: post.id,
    media,
    onSuccess: handlePostSuccess
  });

  const isMutatingChange =
    useIsMutating({ mutationKey: [POST_MUTATION_KEYS.CHANGE] }) > 0;
  const isMutatingPoll =
    useIsMutating({ mutationKey: [POST_MUTATION_KEYS.POLL] }) > 0;
  const isMutating = isMutatingChange || isMutatingPoll;

  const submit = useCallback(() => {
    form.handleSubmit(editPost)();
  }, [form, editPost]);

  const handleOnPress = useThrottledCallback(submit, 1000);

  const isSubmitDisabled =
    !form.formState.isValid || media.isUploadPending || isMutating;

  return (
    <View style={styles.container}>
      <StateView
        isLoading={isMutating}
        isError={false}
        stateLabels={t('state', { returnObjects: true })}
        contentContainerStyle={styles.body}
        backgroundColor={theme.colors.surfaceContainerLow}
        keyboardAware
        keyboardAwareMode="layout"
        keyboardBottomOffset={24}
        gestureScroll
        keyboardShouldPersistTaps="handled"
      >
        <PostForm
          form={form}
          sender={sender}
          media={media}
          modal={modal}
          channelReadonly
          geoAreaDepth={MINISTRY_GEO_AREA_DEPTH}
        />
        <View style={styles.submitButtonContainer}>
          <PostSubmitButton
            label={t('editSend')}
            disabled={isSubmitDisabled}
            onPress={handleOnPress}
          />
        </View>
      </StateView>
    </View>
  );
};
